'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Trash2,
  Tv2,
  Clapperboard,
  Package,
  RefreshCw,
  Webhook,
} from 'lucide-react';
import type { WebhookLogEntry, WebhookLogItem } from '@/lib/types';
import { webhookLogStore } from '@/lib/webhookLogStore';

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function StatusBadge({ status }: { status: WebhookLogEntry['status'] }) {
  if (status === 'success')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-semibold bg-success/15 text-success">
        <CheckCircle2 size={10} /> Success
      </span>
    );
  if (status === 'partial')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-semibold bg-warning/15 text-warning">
        <AlertTriangle size={10} /> Partial
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-semibold bg-danger/15 text-danger">
      <XCircle size={10} /> Failed
    </span>
  );
}

function ItemRow({ item }: { item: WebhookLogItem }) {
  const [expanded, setExpanded] = useState(false);
  const payloadStr = JSON.stringify(item.payload, null, 2);

  return (
    <div className="rounded-lg border border-border overflow-hidden" style={{ background: 'var(--input)' }}>
      <div className="flex items-center gap-3 px-3 py-2.5">
        {item.mediaType === 'series' ? (
          <Tv2 size={13} className="text-sonarr flex-shrink-0" />
        ) : item.mediaType === 'movie' ? (
          <Clapperboard size={13} className="text-radarr flex-shrink-0" />
        ) : (
          <Package size={13} className="text-muted-foreground flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
          {item.sourceInstanceName && (
            <p className="text-2xs text-muted-foreground font-mono-data">{item.sourceInstanceName}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {item.status === 'success' ? (
            <CheckCircle2 size={14} className="text-success" />
          ) : (
            <XCircle size={14} className="text-danger" />
          )}
          <button
            onClick={() => setExpanded((e) => !e)}
            className="btn-ghost p-1 rounded"
            title="Preview payload"
          >
            {expanded ? (
              <ChevronDown size={13} className="text-muted-foreground" />
            ) : (
              <ChevronRight size={13} className="text-muted-foreground" />
            )}
          </button>
        </div>
      </div>
      {item.status === 'error' && item.error && (
        <div className="px-3 pb-2">
          <p className="text-2xs text-danger font-mono-data">{item.error}</p>
        </div>
      )}
      {expanded && (
        <div className="border-t border-border px-3 py-2">
          <p className="text-2xs text-muted-foreground mb-1.5 font-semibold">Payload Preview</p>
          <pre className="text-2xs text-muted-foreground font-mono-data overflow-x-auto scrollbar-thin leading-relaxed max-h-48">
            {payloadStr}
          </pre>
        </div>
      )}
    </div>
  );
}

function LogRow({ entry }: { entry: WebhookLogEntry }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-xl border border-border overflow-hidden" style={{ background: 'var(--card)' }}>
      {/* Summary row */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-white/5 transition-colors"
      >
        {/* Expand chevron */}
        <span className="flex-shrink-0 text-muted-foreground">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>

        {/* Timestamp */}
        <div className="w-44 flex-shrink-0">
          <p className="text-xs font-mono-data text-foreground">{formatTimestamp(entry.timestamp)}</p>
        </div>

        {/* Webhook */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Webhook size={12} className="text-muted-foreground flex-shrink-0" />
            <p className="text-sm font-medium text-foreground truncate">{entry.webhookName}</p>
          </div>
          <p className="text-2xs text-muted-foreground font-mono-data truncate">{entry.webhookUrl}</p>
        </div>

        {/* Source */}
        <div className="w-36 flex-shrink-0 hidden md:block">
          <p className="text-xs text-muted-foreground truncate">{entry.connectionSource}</p>
        </div>

        {/* Item count */}
        <div className="w-16 flex-shrink-0 text-center hidden sm:block">
          <span className="text-sm font-semibold text-foreground">{entry.itemCount}</span>
          <p className="text-2xs text-muted-foreground">items</p>
        </div>

        {/* Status */}
        <div className="flex-shrink-0">
          <StatusBadge status={entry.status} />
        </div>
      </button>

      {/* Expanded items */}
      {expanded && (
        <div className="border-t border-border px-4 py-3 flex flex-col gap-2">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Per-Item Results
            </p>
            <p className="text-2xs text-muted-foreground">
              <span className="text-success font-semibold">{entry.successCount} sent</span>
              {entry.errorCount > 0 && (
                <span className="text-danger font-semibold ml-2">{entry.errorCount} failed</span>
              )}
            </p>
          </div>
          {entry.items.map((item) => (
            <ItemRow key={`log-item-${entry.id}-${item.id}`} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function WebhookLogsScreen() {
  const [logs, setLogs] = useState<WebhookLogEntry[]>([]);
  const [mounted, setMounted] = useState(false);

  const loadLogs = useCallback(() => {
    setLogs(webhookLogStore.getLogs());
  }, []);

  useEffect(() => {
    setMounted(true);
    loadLogs();
  }, [loadLogs]);

  const handleClear = () => {
    webhookLogStore.clearLogs();
    setLogs([]);
  };

  if (!mounted) return null;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <ClipboardList size={16} className="text-primary" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-foreground">Webhook Logs</h1>
            <p className="text-xs text-muted-foreground">
              {logs.length} send{logs.length !== 1 ? 's' : ''} recorded
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadLogs}
            className="btn-ghost flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg"
            title="Refresh"
          >
            <RefreshCw size={13} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          {logs.length > 0 && (
            <button
              onClick={handleClear}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg text-danger hover:bg-danger/10 transition-colors"
              title="Clear all logs"
            >
              <Trash2 size={13} />
              <span className="hidden sm:inline">Clear All</span>
            </button>
          )}
        </div>
      </div>

      {/* Column headers */}
      {logs.length > 0 && (
        <div className="flex items-center gap-4 px-4 py-2 border-b border-border flex-shrink-0">
          <div className="w-5 flex-shrink-0" />
          <div className="w-44 flex-shrink-0">
            <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wider">Timestamp</p>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wider">Webhook</p>
          </div>
          <div className="w-36 flex-shrink-0 hidden md:block">
            <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wider">Source</p>
          </div>
          <div className="w-16 flex-shrink-0 text-center hidden sm:block">
            <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wider">Items</p>
          </div>
          <div className="flex-shrink-0 w-20">
            <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wider">Status</p>
          </div>
        </div>
      )}

      {/* Log list */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 flex flex-col gap-2">
        {logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center py-20">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
              <ClipboardList size={24} className="text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground mb-1">No webhook sends yet</p>
              <p className="text-xs text-muted-foreground max-w-xs">
                Dispatch items from the Media Library and each send will be recorded here with full payload details.
              </p>
            </div>
          </div>
        ) : (
          logs.map((entry) => <LogRow key={`log-entry-${entry.id}`} entry={entry} />)
        )}
      </div>
    </div>
  );
}
